import { LoadingSpinner } from "components/LoadingSpinner";
import { useLoading } from "context/LoadingContext";
import { ToastContext } from "context/ToastContext";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmDialog } from "primereact/confirmdialog";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputSwitch } from "primereact/inputswitch";
import { useMemo, useState, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  useDeleteExplodingFeeScheduleMutation,
  useGetAllExplodingFeeSchedulesQuery,
  useUploadExplodingFeeScheduleCsvMutation,
} from "store/queries/explodingFeeSchedule";
import { useGetAllPayersQuery } from "store/queries/payers";
import { useGetAllProvidersQuery } from "store/queries/providers";
import styled from "styled-components";

import { defaultScopeCell } from "./defaultScopeCell";

const Row = styled.div`
  display: flex;
  flex-direction: row;
`;

const ColumnWrapper = styled.div`
  display: block;
  max-height: 20px;
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export function ExplodingFeeScheduleTable() {
  const [selectedItems, setSelectedItems] = useState<{ id: string }[]>([]);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadProviderId, setUploadProviderId] = useState<string | null>(null);
  const [uploadPayerId, setUploadPayerId] = useState<number | null>(null);
  const [showDefaultsOnly, setShowDefaultsOnly] = useState(false);
  const fileUploadRef = useRef<FileUpload>(null);
  const { setIsLoading } = useLoading();
  const { current: toastElement } = useContext(ToastContext);
  const [deleteRuleset] = useDeleteExplodingFeeScheduleMutation();
  const [uploadCsv] = useUploadExplodingFeeScheduleCsvMutation();
  const navigate = useNavigate();

  const {
    data: rulesets,
    isLoading,
    refetch,
  } = useGetAllExplodingFeeSchedulesQuery(
    showDefaultsOnly ? { isDefault: true } : undefined
  );

  const { data: providersData } = useGetAllProvidersQuery();
  const { data: payersData } = useGetAllPayersQuery();

  const providerOptions = useMemo(() => {
    if (!providersData?.data) return [];
    return providersData.data.map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
  }, [providersData]);

  const payerOptions = useMemo(() => {
    if (!payersData?.data) return [];
    return payersData.data.map((p: any) => ({
      label: p.name,
      value: p.id,
    }));
  }, [payersData]);

  const parsedRulesets = useMemo(() => {
    if (!rulesets?.data) return [];
    return rulesets.data.map((ruleset) => ({
      ...ruleset,
      providerName: ruleset.provider?.name,
      payerName: ruleset.payer?.name,
      totalRateFormatted: `$${(ruleset.totalRateInCents / 100).toFixed(2)}`,
      perCodeRateFormatted: `$${(
        ruleset.totalRateInCents /
        100 /
        ruleset.cptCodes.length
      ).toFixed(2)}`,
    }));
  }, [rulesets]);

  const cptCodesTransformer = (cptCodes: string[]) => {
    const allCodes = cptCodes.join(", ");
    return <ColumnWrapper>{allCodes}</ColumnWrapper>;
  };

  function rulesetAction(rulesetId: string, copy?: boolean) {
    return (
      <Row>
        <Button
          className="p-button-link"
          onClick={() =>
            navigate(
              `/payer-rules/exploding-fee-schedules/${
                copy ? "copy" : "edit"
              }/${rulesetId}`
            )
          }
        >
          <p className="small-text">{copy ? "Copy" : "Edit"}</p>
        </Button>
      </Row>
    );
  }

  const handleDelete = async () => {
    setIsLoading(true);
    try {
      const deletePromises = selectedItems.map((item) =>
        deleteRuleset(item.id)
      );
      await Promise.all(deletePromises);
      await refetch();
      toastElement?.show({
        severity: "success",
        summary: "Deleted!",
        detail: "Exploding fee schedule ruleset(s) deleted successfully.",
      });
    } catch (error) {
      await refetch();
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Error while deleting rulesets",
      });
    } finally {
      setIsLoading(false);
      setSelectedItems([]);
    }
  };

  const handleUpload = async (event: { files: File[] }) => {
    if (!uploadProviderId || !uploadPayerId) {
      toastElement?.show({
        severity: "error",
        summary: "Error",
        detail: "Please select a provider and payer",
      });
      return;
    }

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", event.files[0]);
      formData.append("providerId", uploadProviderId);
      formData.append("payerId", uploadPayerId.toString());

      const result = await uploadCsv(formData).unwrap();
      await refetch();

      let detail = `${result.data.created} rulesets created.`;
      if (result.data.errors.length > 0) {
        detail += ` ${result.data.errors.length} rows had errors.`;
      }

      toastElement?.show({
        severity: "success",
        summary: "Upload Complete",
        detail,
      });
      setShowUploadDialog(false);
      setUploadProviderId(null);
      setUploadPayerId(null);
      fileUploadRef.current?.clear();
    } catch (error: any) {
      toastElement?.show({
        severity: "error",
        summary: "Upload Error",
        detail: error?.data?.message || "Error uploading CSV",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <>
      <div className="flex justify-content-between align-items-center mb-3">
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <span style={{ fontSize: 13 }}>Show defaults only</span>
          <InputSwitch
            checked={showDefaultsOnly}
            onChange={(e) => setShowDefaultsOnly(!!e.value)}
          />
        </div>
        <Button
          icon="pi pi-upload"
          label="Upload CSV"
          className="p-button-outlined"
          onClick={() => setShowUploadDialog(true)}
        />
      </div>

      <DataTable
        rows={10}
        paginator
        stripedRows
        className="p-fluid"
        filterDisplay="row"
        value={parsedRulesets}
        responsiveLayout="scroll"
        style={{ cursor: "pointer" }}
        selection={selectedItems}
        emptyMessage="No exploding fee schedule rulesets to show."
        selectionMode="single"
        onSelectionChange={(event) => setSelectedItems(event.value)}
      >
        <Column selectionMode="multiple" headerStyle={{ width: "3em" }} />
        <Column
          filter
          field="providerName"
          style={{ minWidth: "250px" }}
          header="Provider Name"
          filterPlaceholder="Search by provider"
          body={(row) => defaultScopeCell(row, row.providerName)}
        />
        <Column
          filter
          field="payerName"
          style={{ minWidth: "250px" }}
          header="Payer Name"
          filterPlaceholder="Search by payer"
          body={(row) => defaultScopeCell(row, row.payerName)}
        />
        <Column
          filter
          field="groupName"
          style={{ minWidth: "150px" }}
          header="Group Name"
          filterPlaceholder="Search by group"
        />
        <Column
          filter
          field="cptCodes"
          body={(row) => cptCodesTransformer(row.cptCodes)}
          style={{ minWidth: "280px", maxWidth: "280px" }}
          header="CPT Codes"
          filterPlaceholder="Search by CPT code"
        />
        <Column
          field="totalRateFormatted"
          style={{ minWidth: "120px" }}
          header="Total Rate"
        />
        <Column
          field="perCodeRateFormatted"
          style={{ minWidth: "120px" }}
          header="Per-Code Rate"
        />
        <Column
          style={{ maxWidth: "100px" }}
          body={(row) => rulesetAction(row.id, true)}
        />
        <Column
          style={{ maxWidth: "100px" }}
          body={(row) => rulesetAction(row.id)}
        />
      </DataTable>

      <div className="flex justify-content-end">
        <Button
          label="Delete"
          className="p-button-outlined mt-4"
          disabled={!selectedItems.length}
          onClick={() => {
            confirmDialog({
              header: "Confirmation",
              icon: "pi pi-exclamation-triangle",
              message:
                "Are you sure you want to delete selected exploding fee schedule rulesets?",
              accept: handleDelete,
            });
          }}
        />
      </div>

      <Dialog
        header="Upload Exploding Fee Schedule CSV"
        visible={showUploadDialog}
        style={{ width: "500px" }}
        onHide={() => {
          setShowUploadDialog(false);
          setUploadProviderId(null);
          setUploadPayerId(null);
        }}
      >
        <div className="flex flex-column gap-3">
          <div className="field">
            <label htmlFor="provider">Provider</label>
            <Dropdown
              id="provider"
              value={uploadProviderId}
              options={providerOptions}
              onChange={(e) => setUploadProviderId(e.value)}
              placeholder="Select a provider"
              filter
              className="w-full"
            />
          </div>
          <div className="field">
            <label htmlFor="payer">Payer</label>
            <Dropdown
              id="payer"
              value={uploadPayerId}
              options={payerOptions}
              onChange={(e) => setUploadPayerId(e.value)}
              placeholder="Select a payer"
              filter
              className="w-full"
            />
          </div>
          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            accept=".csv"
            maxFileSize={10000000}
            chooseLabel="Choose CSV File"
            customUpload
            uploadHandler={handleUpload}
            auto={false}
          />
          <Button
            label="Upload"
            disabled={!uploadProviderId || !uploadPayerId}
            onClick={() => fileUploadRef.current?.upload()}
          />
        </div>
      </Dialog>
    </>
  );
}
